import { findKey } from 'lodash'

import type { DataChangeRequestData } from '@albacross/types'

import { getCountryMap } from './countries'
import { getLinkedInIndustryMap } from './industries'
import { findNaceCode, getNaceMap } from './nace'

export const employeesRanges = {
  '0-1': {
    from: 0,
    to: 1,
  },
  '2-10': {
    from: 2,
    to: 10,
  },
  '11-50': {
    from: 11,
    to: 50,
  },
  '51-200': {
    from: 51,
    to: 200,
  },
  '201-500': {
    from: 201,
    to: 500,
  },
  '501-1000': {
    from: 501,
    to: 1000,
  },
  '1001-5000': {
    from: 1001,
    to: 5000,
  },
  '5001-10000': {
    from: 5001,
    to: 10000,
  },
  '10001+': {
    from: 10001,
    to: 10001,
  },
}

export const getEmployeesRange = (from, to) => {
  const exact = findKey(employeesRanges, range => range.from === from && range.to === to)
  if (exact) return exact
  // Rows predating the customer form's 500->501 fix. A null upper bound is deliberately NOT resolved:
  // asEs drops employees entirely when `to` is absent, so such a row must look wrong rather than valid.
  return from === 500 && to === 1000 ? '501-1000' : null
}

export const usersReportingFromServer = (users = []) =>
  users.map(({ user_id: userId, user_name: userName }) => ({
    userId,
    userName,
  }))

// Keyed off DataChangeRequestData so adding a field there fails typecheck until it has a label.
const PROPOSED_FIELD_LABELS: Record<keyof DataChangeRequestData, string> = {
  name: 'Name',
  url: 'Website',
  number: 'Reg. number',
  description: 'Description',
  address: 'Address',
  employees: 'Employees',
  financial_report: 'Revenue',
  socials: 'Socials',
  founded_year: 'Founded',
  nace_code: 'NACE',
  linkedin_industry_code: 'Industry',
  tech: 'Tech',
  internet_provider: 'ISP',
}

// `false` counts: an explicit internet_provider: false is a proposal to un-flag the company.
const isProposed = value => {
  if (value === undefined || value === null || value === '') return false
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object') return Object.values(value).some(isProposed)
  return true
}

export const getProposedFieldLabels = (data): string[] =>
  Object.entries(PROPOSED_FIELD_LABELS)
    .filter(([key]) => isProposed(data?.[key]))
    .map(([, label]) => label)

export const PROPOSED_FIELD_SEPARATOR = ', '

// How many labels fit in `available` px, leaving room for the "+n" chip when any are left over.
export const fitProposedFieldLabels = (
  labels: string[],
  available: number,
  measure: (text: string) => number,
  chipWidth: number,
): number => {
  // Test the whole list first: a tail label narrower than the chip would otherwise be hidden behind it.
  if (measure(labels.join(PROPOSED_FIELD_SEPARATOR)) <= available) return labels.length

  let fits = 0

  for (let i = 0; i < labels.length - 1; i++) {
    if (measure(labels.slice(0, i + 1).join(PROPOSED_FIELD_SEPARATOR)) + chipWidth > available) break
    fits = i + 1
  }

  // Always show something: an ellipsised first label beats a bare count.
  return Math.max(fits, 1)
}

export const getSelectedDataChangeRequetsIds = selectedDataChangeRequests =>
  Object.entries(selectedDataChangeRequests)
    .filter(([_, selected]) => selected)
    .flatMap(([id]) => Number.parseInt(id, 10))

export const normalizeCompanyChangeFormValues = (formValues, isFieldDirty) => {
  const { areEmployeesDirty, isFinancialReportDirty, isPhoneNumberDirty } = dirtyFieldCheck(isFieldDirty)

  const phoneNumberDirty = key => key.includes('phoneNumber') && isPhoneNumberDirty()
  const employeesDirty = key => key.includes('employees') && areEmployeesDirty()
  const financialReportDirty = key => key.includes('financialReport') && isFinancialReportDirty()

  const getDirtyFields = (values, parentKey?: any) =>
    Object.keys(values).reduce((normalizedObject, key) => {
      const formNestedKey = parentKey ? `${parentKey}.${key}` : key
      const value = values[key]

      if (typeof value === 'object' && value !== null) {
        const nested = getDirtyFields(value, formNestedKey)
        if (Object.keys(nested).length) {
          normalizedObject[key] = nested
        }
        return normalizedObject
      }

      // employees/financialReport/phoneNumber must be sent as whole objects if any nested property changed, so mark all their fields dirty.
      if (
        isFieldDirty(formNestedKey) ||
        phoneNumberDirty(formNestedKey) ||
        employeesDirty(formNestedKey) ||
        financialReportDirty(formNestedKey)
      ) {
        normalizedObject[key] = values[key]
      }

      return normalizedObject
    }, {})

  const normalizedValues = getDirtyFields(normalizeCompanyValue(formValues))

  return normalizedValues
}

// Test the PARENT node, not leaves: RHF records an object-registered field (e.g. the employees range select) as a single boolean, so get(dirtyFields, 'employees.from') is undefined even when it changed.
const dirtyFieldCheck = isFieldDirtyFunc => ({
  areEmployeesDirty: () => !!isFieldDirtyFunc('employees'),
  isFinancialReportDirty: () => !!isFieldDirtyFunc('financialReport'),
  isPhoneNumberDirty: () => !!isFieldDirtyFunc('address.phoneNumber'),
})

export const normalizeCompanyValue = values => ({
  ...values,
  linkedInIndustryCode: values.linkedInIndustryCode?.[0],
  naceCode: values.naceCode?.[0],
  address: {
    ...values.address,
    phoneNumber: {
      ...values.address.phoneNumber,
      country: values.address.phoneNumber.country?.[0] || undefined,
    },
  },
})

export const getChangeRequestInitialData = companyData => {
  const naceCode = companyData.company.naceCode && findNaceCode(companyData.company.naceCode)?.code
  const linkedInIndustryCode = companyData.company.linkedInIndustryCode

  const initialValues = {
    ...companyData.company,
    address: {
      ...companyData.company.address,
      phoneNumber: {
        ...companyData.company.address.phoneNumber,
        country: companyData.company.address.phoneNumber.country
          ? [companyData.company.address.phoneNumber.country]
          : [],
      },
    },
    naceCode: naceCode ? [naceCode] : [],
    linkedInIndustryCode: linkedInIndustryCode ? [linkedInIndustryCode] : [],
  }
  return initialValues
}

export const getEditInitialData = proposedData => ({
  ...proposedData,
  naceCode: proposedData.naceCode ? [proposedData.naceCode] : [],
  linkedInIndustryCode: proposedData.linkedInIndustryCode ? [proposedData.linkedInIndustryCode] : [],
  address: {
    ...proposedData.address,
    phoneNumber: {
      ...proposedData.address?.phoneNumber,
      country: proposedData.address?.phoneNumber?.country ? [proposedData.address.phoneNumber.country] : [],
    },
  },
})

export const naces = [...getNaceMap()].map(([code, nace]: any) => ({
  value: code,
  label: `${nace.code} ${nace.name}`,
}))
export const linkedinIndustries = [...getLinkedInIndustryMap()]
  .sort(([, a]: any, [, b]: any) => a.code - b.code)
  .map(([code, industry]: any) => ({ value: code, label: `${industry.code} ${industry.category}` }))
export const countries = [...getCountryMap()].map(([iso, country]: any) => ({
  value: iso,
  label: country.printableName,
}))

export const employeesOptions = Object.entries({
  '0-1': '0-1',
  '2-10': '2-10',
  '11-50': '11-50',
  '51-200': '51-200',
  '201-500': '201-500',
  '501-1000': '501-1000',
  '1001-5000': '1001-5000',
  '5001-10000': '5001-10000',
  '10001+': '10001+',
}).map(([value, label]) => ({ value, label }))
